import { EncabezadoQaFoto, FotoQa, ProyectoQaFoto, ReporteEmitido } from "src/app/shared/model/calidad-codigo/calidad-codigo.model";
import { ESTADO_PROYECTO, FILTROS_TABLERO, estadoDe } from "../cc-estados";
import {
  SIN_DATO,
  commitCorto,
  diasEnTexto,
  entero,
  fechaCorta,
  fechaHora,
  plural,
  textoCriterios,
  textoUmbral,
  valorMetrica,
} from "../cc-formato";
import { textoConsultaPdf } from "./actualizacion-cc";
import { PdfCc, emitidoTexto } from "./pdf-cc";

/**
 * El PDF del reporte para QA (folio QA-…), armado SOLO con la foto congelada
 * del folio: portada con el veredicto de cada proyecto incluido y una sección
 * por cada proyecto que no pasa (criterios que fallan y desde cuándo, cuántos
 * análisis lleva así, antigüedad del análisis, commit y aceptados), según lo
 * que se pidió en el diálogo; el pie dice si al emitir se consultó SonarQube
 * (foto.actualizacion). Se descarga como <folio>.pdf.
 */
export function descargarPdfQa(reporte: ReporteEmitido): void {
  const foto = reporte.datos as FotoQa | null | undefined;
  if (!foto) {
    return;
  }
  const e = foto.encabezado;
  const proyectos = foto.proyectos ?? [];
  const doc = new PdfCc();
  doc.encabezado("Reporte para QA", "Quality Gate de los proyectos en SonarQube: qué falla y desde cuándo", [
    `Folio ${reporte.folio}`,
    emitidoTexto(e?.fecha ?? reporte.fecha, e?.emitidoPor ?? reporte.emitidoPor),
    `Proyectos: ${alcanceTexto(e, proyectos.length)}`,
  ]);
  doc.parrafo(resumen(foto));
  // El nombre del proyecto con ancho propio (40 mm) y la lista de criterios con el resto: sin nombres partidos en tres.
  doc.titulo("Veredicto por proyecto");
  doc.tabla(
    [
      { titulo: "Proyecto", ancho: 40 },
      { titulo: "Gate", ancho: 21 },
      { titulo: "Resultado", ancho: 19, resultado: true },
      { titulo: "Criterios que fallan" },
      { titulo: "No pasa desde", ancho: 22 },
      { titulo: "Último análisis", ancho: 27 },
      { titulo: "Días", ancho: 11, derecha: true },
    ],
    proyectos.map((p) => filaProyecto(p)),
  );
  const noPasan = proyectos.filter((p) => p.estado === "NO_PASA");
  const contenido = e?.contenido ?? { criterios: true, aceptados: true, trazabilidad: true };
  if (contenido.criterios && noPasan.length > 0) {
    for (const p of noPasan) {
      seccionProyecto(doc, p, contenido.aceptados, contenido.trazabilidad);
    }
  } else if (noPasan.length === 0) {
    doc.parrafo("Ningún proyecto del reporte tiene criterios que fallen.");
  }
  const version = e?.fuente?.version ? ` (${e.fuente.version})` : "";
  const cuantos = plural(proyectos.length, "proyecto", "proyectos");
  // Como el del proyecto: si al emitir se consultó SonarQube (una pasada de cada proyecto) o salió con la última foto.
  doc.terminar(
    {
      fuente: [
        `Fuente: SonarQube ${e?.fuente?.urlWeb ?? ""}${version} · consolidado de ${cuantos} con su último análisis guardado en el COS.`,
        textoConsultaPdf(foto.actualizacion),
        "El folio guarda la foto y se vuelve a imprimir igual.",
      ]
        .filter(Boolean)
        .join(" "),
      folio: reporte.folio,
      huella: reporte.huella,
    },
    `${reporte.folio}.pdf`,
  );
}

/**
 * "los 12 del tablero", "6 con el filtro «No pasa»", "3 elegidos a mano",
 * "4 con la búsqueda «pos» y el gate «Sonar way»": cómo se eligieron. El
 * backend congela el filtro como clave (TODOS, NO_PASA…), nunca se imprime cruda.
 */
function alcanceTexto(e: EncabezadoQaFoto | null | undefined, cuantos: number): string {
  const n = entero(e?.proyectosIncluidos ?? cuantos);
  if (e?.seleccion === "ELEGIDOS") {
    return `${n} elegidos a mano`;
  }
  if (e?.seleccion === "NO_PASAN") {
    return `${n}, solo los que no pasan`;
  }
  const partes: string[] = [];
  const filtro = FILTROS_TABLERO.find((f) => f.valor === e?.filtro);
  if (e?.filtro && filtro?.valor !== "TODOS") {
    partes.push(`el filtro «${filtro?.texto ?? e.filtro}»`);
  }
  if (e?.busqueda) {
    partes.push(`la búsqueda «${e.busqueda}»`);
  }
  if (e?.gate) {
    partes.push(`el gate «${e.gate}»`);
  }
  return partes.length > 0 ? `${n} con ${partes.join(" y ")}` : `los ${n} del tablero`;
}

function resumen(foto: FotoQa): string {
  const e = foto.encabezado;
  const n = e?.proyectosIncluidos ?? foto.proyectos?.length ?? 0;
  const noPasan = e?.noPasan ?? (foto.proyectos ?? []).filter((p) => p.estado === "NO_PASA").length;
  const pasan = e?.pasan ?? (foto.proyectos ?? []).filter((p) => p.estado === "PASA").length;
  const sinAnalisis = e?.sinAnalisis ?? (foto.proyectos ?? []).filter((p) => p.estado === "SIN_ANALISIS").length;
  const desactualizados = (foto.proyectos ?? []).filter((p) => p.desactualizado).length;
  const viejos = desactualizados > 0 ? ` ${plural(desactualizados, "lleva", "llevan")} más días sin analizarse que el umbral configurado.` : "";
  const conteo = `${plural(n, "proyecto", "proyectos")}: ${entero(noPasan)} no ${noPasan === 1 ? "pasa" : "pasan"}, ${plural(pasan, "pasa", "pasan")} y ${entero(sinAnalisis)} sin análisis.`;
  return `${conteo}${viejos} El veredicto es el de SonarQube; «pasan X de N» cuenta los criterios del gate actual de cada proyecto.`;
}

function filaProyecto(p: ProyectoQaFoto): string[] {
  const estado = estadoDe(ESTADO_PROYECTO, p.estado);
  const fallan = p.estado === "NO_PASA" ? (p.criteriosQueFallan ?? []).map((c) => c.nombre).join(", ") || textoCriterios(p.criterios) : "—";
  return [
    p.nombre,
    p.gateNombre ?? SIN_DATO,
    estado.texto,
    p.estado === "SIN_ANALISIS" ? "Sin análisis" : fallan,
    p.estado === "NO_PASA" && p.estadoDesde ? fechaCorta(p.estadoDesde) : "—",
    p.fechaAnalisis ? fechaHora(p.fechaAnalisis) : "nunca",
    p.diasDesdeAnalisis != null ? entero(p.diasDesdeAnalisis) : "—",
  ];
}

/** " · 11 análisis seguidos" o " · 1 análisis"; vacío sin dato. */
function seguidosTexto(n: number | null | undefined): string {
  if (n == null) {
    return "";
  }
  return n === 1 ? ` · ${n} análisis` : ` · ${n} análisis seguidos`;
}

function seccionProyecto(doc: PdfCc, p: ProyectoQaFoto, conAceptados: boolean, conTrazabilidad: boolean): void {
  const desde = p.estadoDesde ? ` · no pasa desde el ${fechaCorta(p.estadoDesde)}${seguidosTexto(p.analisisSeguidos)}` : "";
  // El título con al menos la cabecera y dos renglones de su tabla: nunca solo al pie de la página.
  doc.asegura(40);
  doc.titulo(`${p.nombre} · Quality Gate «${p.gateNombre ?? "—"}»${desde}`);
  doc.tabla(
    [
      { titulo: "Criterio que falla" },
      { titulo: "Código", ancho: 22 },
      { titulo: "Debe ser", ancho: 24 },
      { titulo: "Valor", ancho: 24, derecha: true },
      { titulo: "Falla desde", ancho: 26 },
      { titulo: "Seguidos", ancho: 18, derecha: true },
    ],
    (p.criteriosQueFallan ?? []).map((c) => [
      c.nombre,
      c.codigoNuevo ? "Nuevo" : "General",
      c.operador ? textoUmbral({ metrica: c.metrica, operador: c.operador, umbral: c.umbral }) : valorMetrica(c.metrica, c.umbral, c.umbral),
      valorMetrica(c.metrica, c.valor),
      c.desde ? fechaCorta(c.desde) : "—",
      c.analisisSeguidos != null ? entero(c.analisisSeguidos) : "—",
    ]),
  );
  const lineas: string[] = [];
  const antiguedad = p.diasDesdeAnalisis != null ? `${diasEnTexto(p.diasDesdeAnalisis)} sin analizar` : "sin fecha de análisis";
  lineas.push(`Último análisis: ${p.fechaAnalisis ? fechaHora(p.fechaAnalisis) : "nunca"} (${antiguedad})${p.desactualizado ? " · desactualizado" : ""}.`);
  if (conTrazabilidad) {
    const commit = commitCorto(p.revision) || SIN_DATO;
    const liga = p.enlaceCommit ? ` · ${p.enlaceCommit}` : " · sin liga a Azure DevOps configurada";
    lineas.push(`Commit del análisis: ${commit}${liga}.`);
  }
  if (conAceptados) {
    lineas.push(`Hallazgos aceptados: ${entero(p.aceptados?.nuevos ?? null)} en código nuevo · ${entero(p.aceptados?.total ?? null)} en total.`);
  }
  doc.parrafo(lineas.join(" "));
}
